import { Controller } from "@hotwired/stimulus";

// A note's Details blocks below xxl (LI-329; see notes/details/component.html.haml). Sharing, Files and
// Reminders are cards from xxl; below it each chip under the header opens only its own block as a
// dialog: a bottom sheet on a phone, a modal on a tablet. A "+" chip, or the header's ⋯ menu, also
// starts that block's add action (the file picker, the new-reminder form). From xxl, where the cards
// are already on screen, they only start the action. A dialog closes on its close button, the backdrop
// or Escape, and keeps Tab inside it while it's open.
const CARDS = "(min-width: 1400px)"; // Bootstrap's xxl, where the blocks are cards (_note_page.scss)
const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])';

export default class extends Controller {
  static targets = ["block", "backdrop"];

  connect() {
    this.trapFocus = this.trapFocus.bind(this);
  }

  disconnect() {
    this.close();
  }

  open({ params: { block } = {}, currentTarget } = {}) {
    const dialog = this.blockNamed(block);
    if (!dialog || this.cardsShown || this.dialog) return;

    this.dialog = dialog;
    this.opener = currentTarget;
    dialog.classList.add("is-open");
    dialog.setAttribute("role", "dialog");
    dialog.setAttribute("aria-modal", "true");
    dialog.setAttribute("aria-labelledby", dialog.querySelector(".note-details__label").id);
    this.backdropTarget.hidden = false;
    document.body.classList.add("note-sheet-open");
    document.addEventListener("keydown", this.trapFocus);
    dialog.querySelector(".note-details__close").focus({ preventScroll: true });
  }

  start(event) {
    this.open(event);
    const block = this.blockNamed(event.params.block);
    if (!this.cardsShown && this.dialog !== block) return; // another block's dialog is in the way

    block?.querySelector("[data-note-details-start]")?.click();
  }

  // Escape is left to a Bootstrap modal opened from the dialog (rename, move, preview), which keeps focus
  // while it shows. By the time the key reaches the document that modal has already hidden itself, so
  // what tells is where the key was pressed.
  close(event) {
    if (!this.dialog) return;
    if (event?.type === "keydown" && event.target.closest?.(".modal")) return;

    this.dialog.classList.remove("is-open");
    ["role", "aria-modal", "aria-labelledby"].forEach((name) => this.dialog.removeAttribute(name));
    this.backdropTarget.hidden = true;
    document.body.classList.remove("note-sheet-open");
    document.removeEventListener("keydown", this.trapFocus);
    this.opener?.focus();
    this.dialog = null;
    this.opener = null;
  }

  // Tab from the dialog's last control wraps to its first, and Shift+Tab the other way. A Bootstrap
  // modal opened on top of the dialog keeps its own focus.
  trapFocus(event) {
    if (event.key !== "Tab" || document.querySelector(".modal.show")) return;

    const focusable = [...this.dialog.querySelectorAll(FOCUSABLE)].filter((el) => el.offsetParent !== null);
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const inside = this.dialog.contains(document.activeElement);
    if (event.shiftKey && (!inside || document.activeElement === first)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && (!inside || document.activeElement === last)) {
      event.preventDefault();
      first.focus();
    }
  }

  blockNamed(name) {
    return this.blockTargets.find((block) => block.dataset.noteDetailsBlock === name);
  }

  get cardsShown() {
    return window.matchMedia(CARDS).matches;
  }
}
